import React, { useEffect, useState } from "react";
import { Container, Tab, Nav, Row, Col, Button, Spinner } from "react-bootstrap";
import { User } from "../../types/user";
import { fetchUserCases, fetchUsers } from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import UserTab from "./UserTab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouseChimneyUser, faUser, faUserCog, faUserLock, faUserShield, faUserTie } from "@fortawesome/free-solid-svg-icons";
import ToastMsg from "../ui/ToastMsg";
import useApp from "../../utils/useApp";
import GuardianTab from "./GuardianTab";
import useMenuToggle from "../../utils/useMenuToggle";

const Users = () => {
  const { auth, showMenu }: any = useApp();
  const toggleMenu = useMenuToggle();
  const [users, setUsers] = useState<User[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [orgAdminUsers, setOrgAdminUsers] = useState<User[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [caseManagerUsers, setCaseManagerUsers] = useState<User[]>([]);
  const [resUsers, setResUsers] = useState<User[]>([]);
  const [supportUsers, setSupportUsers] = useState<User[]>([]);
  const [guardianUsers, setGuardianUsers] = useState<User[]>([]);
  const [numberUsers, setNumberUsers] = useState<number>(0);
  const [activeOrgAdmin, setActiveOrgAdmin] = useState<boolean>(false);
  const [activeAdmin, setActiveAdmin] = useState<boolean>(false);
  const [activeCaseManagers, setActiveCaseManagers] = useState<boolean>(false);
  const [activeRes, setActiveRes] = useState<boolean>(false);
  const [activeSupport, setActiveSupport] = useState<boolean>(false);
  const [activeGuardian, setActiveGuardian] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [currentDescText, setCurrentDescText] = useState<string>("");

  useEffect(() => {
    setCurrentDescText(`Org Admin - Access Admin plus Organization

Admin - Access Case Manager plus Users, Cases, and Reports

Case Manager - Access to My Cases, Groups, and Announcements

${auth?.profile?.includeHMIS && `Residential Support - Access to Cases and Shelter`}

Support - Access to Messaging in My Cases

Primary Adults - Access to their own case`);
    setActiveOrgAdmin(true);
    setActiveAdmin(false);
    setActiveCaseManagers(false);
    setActiveSupport(false);
    setActiveRes(false);
    setActiveGuardian(false);
    getUsers();

    // trackNavigationEvent("switch_view", "admin_users");
  }, [auth?.profile?.userOrgID]);

  useEffect(() => {
    const updatedUsers = (workingUsers: User[]) =>
      workingUsers.map((user) => {
        const filteredCases = cases.filter(
          (c: any) =>
            c.primaryCM?.user_id === user?.id || c.secondaryCM?.user_id === user?.id || c.support?.some((s: any) => s.user_id === user?.id),
        );

        const activeCases = filteredCases.filter((c: any) => c.casestatus === "active");
        const inactiveCases = filteredCases.filter((c: any) => c.casestatus === "inactive");
        const waitlistCases = filteredCases.filter((c: any) => c.casestatus === "waitlist");

        return {
          ...user,
          cases: filteredCases,
          totalCases: filteredCases.length,
          activeCases: activeCases.length,
          inactiveCases: inactiveCases.length,
          waitlistCases: waitlistCases.length,
        };
      });

    var workingUsers: User[];
    workingUsers = users.filter((x: any) => x.role === "org_admin");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setOrgAdminUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "admin");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setAdminUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "case_manager");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setCaseManagerUsers(workingUsers);
    }

    //isHMIS
    setResUsers(users.filter((x: any) => x.role === "res_support"));

    workingUsers = users.filter((x: any) => x.role === "support");
    if (cases) {
      workingUsers = updatedUsers(workingUsers);
      setSupportUsers(workingUsers);
    }

    workingUsers = users.filter((x: any) => x.role === "family");
    const updatedFamilyUsers = workingUsers
      .map((user) => {
        let caseName = undefined;
        cases.forEach((c: any) => {
          if (c.casestatus === "active" && c.family.some((f: any) => f.user_id === user.id)) {
            caseName = c.name;
          }
        });

        return {
          ...user,
          casename: caseName,
        };
      })
      .filter((user) => user.casename !== undefined);
    setGuardianUsers(updatedFamilyUsers);
  }, [users, cases]);

  const getUsers = async () => {
    setIsLoading(true);
    await getCases();

    fetchUsers()
      .then((res: any) => {
        const userList = res[0].data;
        setUsers(userList);

        var workingUsers: User[];
        workingUsers = userList.filter((x: any) => x.role === "org_admin");
        setOrgAdminUsers(workingUsers);

        workingUsers = userList.filter((x: any) => x.role === "admin");
        setAdminUsers(workingUsers);

        workingUsers = userList.filter((x: any) => x.role === "case_manager");
        setCaseManagerUsers(workingUsers);

        //isHMIS
        setResUsers(userList.filter((x: any) => x.role === "res_support"));

        setSupportUsers(userList.filter((x: any) => x.role === "support"));

        var totalUsers = [];
        if (auth?.profile?.includeHMIS) {
          totalUsers = userList.filter(
            (x: any) =>
              x.role === "support" || x.role === "res_support" || x.role === "case_manager" || x.role === "admin" || x.role === "org_admin",
          );
        } else {
          totalUsers = userList.filter(
            (x: any) => x.role === "support" || x.role === "case_manager" || x.role === "admin" || x.role === "org_admin",
          );
        }
        setNumberUsers(totalUsers.length);

        workingUsers = userList.filter((x: any) => x.role === "family");
        setGuardianUsers(workingUsers);

        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCases = async () => {
    await fetchUserCases()
      .then((res: any) => {
        var c = res[0].data;
        setCases(c);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onNavClick = (route: string) => {
    // if (route) {
    //   trackNavigationEvent("switch_view", route + "_users");
    // }

    switch (route) {
      case "org_admin":
        setActiveOrgAdmin(true);
        setActiveAdmin(false);
        setActiveCaseManagers(false);
        setActiveRes(false);
        setActiveSupport(false);
        setActiveGuardian(false);
        break;
      case "admin":
        setActiveOrgAdmin(false);
        setActiveAdmin(true);
        setActiveCaseManagers(false);
        setActiveRes(false);
        setActiveSupport(false);
        setActiveGuardian(false);
        break;
      case "case_manager":
        setActiveOrgAdmin(false);
        setActiveAdmin(false);
        setActiveCaseManagers(true);
        setActiveRes(false);
        setActiveSupport(false);
        setActiveGuardian(false);
        break;
      case "res_support":
        setActiveOrgAdmin(false);
        setActiveAdmin(false);
        setActiveCaseManagers(false);
        setActiveRes(true);
        setActiveSupport(false);
        setActiveGuardian(false);
        break;
      case "support":
        setActiveOrgAdmin(false);
        setActiveAdmin(false);
        setActiveCaseManagers(false);
        setActiveRes(false);
        setActiveSupport(true);
        setActiveGuardian(false);
        break;
      case "guardian":
        setActiveOrgAdmin(false);
        setActiveAdmin(false);
        setActiveCaseManagers(false);
        setActiveRes(false);
        setActiveSupport(false);
        setActiveGuardian(true);
        break;
      default:
        setActiveOrgAdmin(true);
        setActiveAdmin(false);
        setActiveCaseManagers(false);
        setActiveRes(false);
        setActiveSupport(false);
        setActiveGuardian(false);
        break;
    }
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu custom-container px-5" : "lg div-main custom-container px-5"}>
          <Tab.Container defaultActiveKey="org_admin">
            <Row>
              <Col xs={12} sm={12} md={3}>
                <Nav
                  variant="pills"
                  className={showMenu ? "flex-column sticky-top mycase-menu-open-sticky" : "flex-column sticky-top mycase-menu-sticky"}
                >
                  <Nav.Item className="mb-2">
                    <PageTitle title={"Users (" + numberUsers + ")"} descriptionText={currentDescText} />
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      data-testid="org-admin-tab"
                      eventKey="org_admin"
                      className={activeOrgAdmin ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("org_admin")}
                    >
                      <>
                        <FontAwesomeIcon icon={faUserLock} className={"me-2"} />
                        {toggleMenu ? "Org Admin (" + orgAdminUsers.length + ")" : "Org Admin"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      data-testid="admin-tab"
                      eventKey="admin"
                      className={activeAdmin ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("admin")}
                    >
                      <>
                        <FontAwesomeIcon icon={faUserCog} className={"me-2"} />
                        {toggleMenu ? "Admin (" + adminUsers.length + ")" : "Admin"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      data-testid="case-manager-tab"
                      eventKey="case_manager"
                      className={activeCaseManagers ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("case_manager")}
                    >
                      <>
                        <FontAwesomeIcon icon={faUser} className={"me-2"} />
                        {toggleMenu ? "Case Manager (" + caseManagerUsers.length + ")" : "Case Mgr"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  {auth?.profile?.includeHMIS === 1 && (
                    <Nav.Item className="d-grid">
                      <Nav.Link
                        data-testid="res-support-tab"
                        eventKey="res_support"
                        className={activeRes ? "nav-vertical-active" : "nav-vertical"}
                        onClick={() => onNavClick("res_support")}
                      >
                        <>
                          <FontAwesomeIcon icon={faHouseChimneyUser} className={"me-2"} />
                          {toggleMenu ? "Residential Support (" + resUsers.length + ")" : "Residential"}
                        </>
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      data-testid="support-tab"
                      eventKey="support"
                      className={activeSupport ? "nav-vertical-active" : "nav-vertical"}
                      onClick={() => onNavClick("support")}
                    >
                      <>
                        <FontAwesomeIcon icon={faUserTie} className={"me-2"} />
                        {toggleMenu ? "Support (" + supportUsers.length + ")" : "Support"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <hr />
                  </Nav.Item>
                  <Nav.Item className="d-grid">
                    <Nav.Link
                      data-testid="guardian-tab"
                      eventKey="guardian"
                      className={activeGuardian ? "nav-vertical-active mb-3" : "nav-vertical mb-3"}
                      onClick={() => onNavClick("guardian")}
                    >
                      <>
                        <FontAwesomeIcon icon={faUserShield} className={"me-2"} />
                        {toggleMenu ? "Primary Adults" : "Primary"}
                      </>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col xs={12} sm={12} md={9} className="mt-2">
                <Tab.Content>
                  <Tab.Pane eventKey="org_admin">
                    <UserTab
                      users={orgAdminUsers}
                      setUsers={setUsers}
                      setCases={setCases}
                      type="org_admin"
                      displayType="Org Admin"
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="admin">
                    <UserTab
                      users={adminUsers}
                      setUsers={setUsers}
                      setCases={setCases}
                      type="admin"
                      displayType="Admin"
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="case_manager">
                    <UserTab
                      users={caseManagerUsers}
                      setUsers={setUsers}
                      setCases={setCases}
                      type="case_manager"
                      displayType="Case Manager"
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="res_support">
                    <UserTab
                      users={resUsers}
                      setUsers={setUsers}
                      setCases={setCases}
                      type="res_support"
                      displayType="Residential Support"
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="support">
                    <UserTab
                      users={supportUsers}
                      setUsers={setUsers}
                      setCases={setCases}
                      type="support"
                      displayType="Support"
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="guardian">
                    <GuardianTab users={guardianUsers} />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      )}
    </>
  );
};

export default Users;
